import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { FieldType } from '../magic-report.type';
import { DeclarativeLayoutRenderer } from './DeclarativeLayoutRenderer';
import { MATERIAL_TESTING_REPORTS_SECTION_CONFIG } from './sections.config';
import { MaterialTestingTable, DEFAULT_TESTS } from './MaterialTestingTable';
import { MaterialTestingFailedTestsTable } from './MaterialTestingFailedTestsTable';
import { allTestsNeverReceived, allTestsNeverReceivedAndNoneCurrent } from './material-testing.util';

interface MaterialTestingReportsSectionProps {
  magicReportId: string;
}

interface TestData {
  testName: string;
  everReceived: 'Yes' | 'No';
  anyCurrent: 'Yes' | 'No';
  failedTests: string;
}

const TESTS_FIELD = 'MaterialTestingTests';
const AUTO_COMMENT_FIELD = 'MaterialTestingAutoComment';
const FAILED_TESTS_COMMENT_FIELD = 'MaterialTestingFailedTestsComment';
const ALL_PASS_COMMENT_FIELD = 'MaterialTestingAllPassComment';
const RECOMMENDATION_COMMENT_FIELD = 'MaterialTestingRecommendationComment';
const PROVIDER_COMMENT_FIELD = 'MaterialTestingProviderComment';
const REQUIRE_TESTING_FIELD = 'RequireTestingForRetainageRelease';
const REMAINING_TESTS_PASSED_FIELD = 'MaterialTestingRemainingTestsPassed';
const ENGINEER_RESPONDING_FIELD = 'MaterialTestingEngineerResponding';
const COMMENTS_FIELD = 'Comments';
const REQUIRE_TESTING_COMMENT =
  'The project is now considered substantially complete. However we have not received any Materials Testing Reports for the project to confirm detailed compliance with the requirements of the specifications for soil compaction, concrete strength, reinforcing steel observations, or other materials. We recommend holding Retainage until a letter is provided by the engineer responsible for this work, stating that all reports related materials testing either met or exceeded the requirements of the specifications.';
const PROVIDER_PLACEHOLDER = 'XXXXX';
const DEFAULT_PROVIDER_COMMENT = `Testing work is being provided by ${PROVIDER_PLACEHOLDER}.`;
const ENGINEER_RESPONDING_COMMENT =
  'We will recommend the Engineer of Record, who is responsible for these material specifications, review and comment on the failed tests and provide any necessary recommendations.';
const ENGINEER_NOT_RESPONDING_COMMENT =
  'Materials Testing Reports reveal Work which did not meet the requirements of the Specifications Attempts to receive retests on the failed Work have been unsuccessful. Due to these circumstances, we will recommend holding all Retainage from the Contractor until we receive a letter from the Materials Testing Engineer with the following statement included: "All work tested or retested by this office has met or exceeded the requirements of the specifications on the project." Any exceptions to this statement should be specifically noted with an explanation and recommendation from the Testing Engineer and the Engineer(s) of Record.';

const parseTestData = (value: string): TestData | null => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const MaterialTestingReportsSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const testsField = getField(TESTS_FIELD);
  const requireTestingField = getField(REQUIRE_TESTING_FIELD);
  const testsEntries = testsField?.entries || [];

  const tests: TestData[] = useMemo(() => {
    if (testsEntries.length === 0) {
      return DEFAULT_TESTS;
    }
    return testsEntries.map(entry => parseTestData(entry.value || '')).filter((test): test is TestData => test !== null);
  }, [testsEntries]);

  // Track previous values to only update when they actually change
  // Initialize with null to detect first mount
  const prevValuesRef = useRef<{ tests: string; requireTesting: string; remainingPassed: string; engineerResponding: string } | null>(null);

  // Helper functions to generate auto-comments
  const generateAutoComment = (tests: TestData[], requireTestingValue: string): string => {
    const allNeverReceived = allTestsNeverReceived(tests);
    const allReceived = tests.every(test => test.everReceived === 'Yes');
    const someReceived = tests.some(test => test.everReceived === 'Yes');
    const hasCurrent = tests.some(test => test.anyCurrent === 'Yes');
    const currentTests = tests.filter(test => test.anyCurrent === 'Yes');
    const notReceivedTests = tests.filter(test => test.everReceived === 'No');
    const allEverAndCurrentNo = allTestsNeverReceivedAndNoneCurrent(tests);

    if (requireTestingValue === 'Yes' && allEverAndCurrentNo) {
      return REQUIRE_TESTING_COMMENT;
    }

    if (allNeverReceived) {
      return "We have not received copies of Materials Testing Reports and will request of the borrower that we be placed on the Testing Engineer's distribution list.";
    } else if (!hasCurrent && someReceived && !allReceived) {
      // Only include the "not yet received" part if there are actually tests not received
      const notReceivedNames = notReceivedTests.map(t => t.testName).join(', ');
      return `We have received no further copies of Materials Testing Reports since our last report. We have not yet received testing reports to date for ${notReceivedNames}.`;
    } else if (allReceived && !hasCurrent) {
      return 'We have received no further copies of Materials Testing Reports since our last report.';
    } else if (hasCurrent) {
      const currentTestNames = currentTests.map(t => t.testName).join('\n');
      const notReceivedNames = notReceivedTests.map(t => t.testName).join(', ');

      if (notReceivedTests.length > 0) {
        return `We received copies of Materials Testing Reports as listed immediately below since our last report. We have not yet received testing reports to date for ${notReceivedNames}.\n\n${currentTestNames}`;
      }

      return `We received copies of Materials Testing Reports as listed immediately below since our last report.\n\n${currentTestNames}`;
    }
    return '';
  };

  const generateFailedTestsComment = (tests: TestData[]): string => {
    const hasFailed = tests.some(test => test.failedTests && test.failedTests.trim() !== '');
    if (!hasFailed) return '';

    return 'The following is a list of all material tests that revealed work, which did not meet the requirements of the specifications, and for which we have not observed any retests or have not been addressed by the project engineer.';
  };

  const generateAllPassComment = (tests: TestData[], remainingTestsPassed: string): string => {
    const hasFailed = tests.some(test => test.failedTests && test.failedTests.trim() !== '');
    const hasCurrent = tests.some(test => test.anyCurrent === 'Yes');

    // If any test is failing, only show this comment when remaining tests are confirmed as passed
    if (hasFailed) {
      if (remainingTestsPassed !== 'Yes') {
        return '';
      }
      return 'All other provided tests state that materials met or exceeded the requirements of the specifications.';
    }

    // If no tests are failing but there are current tests, show the all pass comment
    if (hasCurrent) {
      return 'All provided tests state that materials met or exceeded the requirements of the specifications.';
    }

    return '';
  };

  const generateRecommendationComment = (tests: TestData[], engineerRespondingValue: string): string => {
    const hasFailed = tests.some(test => test.failedTests && test.failedTests.trim() !== '');
    if (!hasFailed) return '';

    return engineerRespondingValue === 'No' ? ENGINEER_NOT_RESPONDING_COMMENT : ENGINEER_RESPONDING_COMMENT;
  };

  // Refresh handlers
  const requireTestingValue = requireTestingField?.value || 'No';

  const handleRefreshAutoComment = () => {
    const autoComment = generateAutoComment(tests, requireTestingValue);
    handleFieldChange(AUTO_COMMENT_FIELD, { value: autoComment });
  };

  const handleRefreshFailedTestsComment = () => {
    const failedComment = generateFailedTestsComment(tests);
    handleFieldChange(FAILED_TESTS_COMMENT_FIELD, { value: failedComment });
  };

  const handleRefreshAllPassComment = () => {
    const allPassComment = generateAllPassComment(tests, remainingTestsPassedField?.value || 'Yes');
    handleFieldChange(ALL_PASS_COMMENT_FIELD, { value: allPassComment });
  };

  const handleRefreshRecommendationComment = () => {
    const recommendationComment = generateRecommendationComment(tests, engineerRespondingField?.value || 'Yes');
    handleFieldChange(RECOMMENDATION_COMMENT_FIELD, { value: recommendationComment });
  };

  const handleRefreshProviderComment = () => {
    handleFieldChange(PROVIDER_COMMENT_FIELD, { value: DEFAULT_PROVIDER_COMMENT });
  };

  const remainingTestsPassedField = getField(REMAINING_TESTS_PASSED_FIELD);
  const engineerRespondingField = getField(ENGINEER_RESPONDING_FIELD);

  // Generate auto-comment based on test states
  // Only update when tests or requireTestingValue actually change
  useEffect(() => {
    const currentTestsString = JSON.stringify(tests);
    const currentRequireTesting = requireTestingValue;
    const currentRemainingPassed = remainingTestsPassedField?.value || 'Yes';
    const currentEngineerResponding = engineerRespondingField?.value || 'Yes';

    // On initial mount or when values actually change
    const isInitialMount = prevValuesRef.current === null;
    const testsChanged = !isInitialMount && prevValuesRef.current!.tests !== currentTestsString;
    const requireTestingChanged = !isInitialMount && prevValuesRef.current!.requireTesting !== currentRequireTesting;
    const remainingPassedChanged = !isInitialMount && prevValuesRef.current!.remainingPassed !== currentRemainingPassed;
    const engineerRespondingChanged = !isInitialMount && prevValuesRef.current!.engineerResponding !== currentEngineerResponding;

    if (isInitialMount || testsChanged || requireTestingChanged || remainingPassedChanged || engineerRespondingChanged) {
      // Update comments when underlying data changes
      const autoComment = generateAutoComment(tests, currentRequireTesting);
      // On initial mount, only populate if empty. On subsequent changes, always update.
      if (isInitialMount) {
        const currentAutoComment = getField(AUTO_COMMENT_FIELD)?.value || '';
        if (!currentAutoComment) {
          handleFieldChange(AUTO_COMMENT_FIELD, { value: autoComment });
        }
      } else {
        handleFieldChange(AUTO_COMMENT_FIELD, { value: autoComment });
      }

      const failedComment = generateFailedTestsComment(tests);
      if (isInitialMount) {
        const currentFailedComment = getField(FAILED_TESTS_COMMENT_FIELD)?.value || '';
        if (!currentFailedComment) {
          handleFieldChange(FAILED_TESTS_COMMENT_FIELD, { value: failedComment });
        }
      } else {
        handleFieldChange(FAILED_TESTS_COMMENT_FIELD, { value: failedComment });
      }

      const allPassComment = generateAllPassComment(tests, currentRemainingPassed);
      if (isInitialMount) {
        const currentAllPassComment = getField(ALL_PASS_COMMENT_FIELD)?.value || '';
        if (!currentAllPassComment) {
          handleFieldChange(ALL_PASS_COMMENT_FIELD, { value: allPassComment });
        }
      } else {
        handleFieldChange(ALL_PASS_COMMENT_FIELD, { value: allPassComment });
      }

      const recommendationComment = generateRecommendationComment(tests, currentEngineerResponding);
      if (isInitialMount) {
        const currentRecommendationComment = getField(RECOMMENDATION_COMMENT_FIELD)?.value || '';
        if (!currentRecommendationComment) {
          handleFieldChange(RECOMMENDATION_COMMENT_FIELD, { value: recommendationComment });
        }
      } else {
        handleFieldChange(RECOMMENDATION_COMMENT_FIELD, { value: recommendationComment });
      }

      // Update ref with new values
      prevValuesRef.current = {
        tests: currentTestsString,
        requireTesting: currentRequireTesting,
        remainingPassed: currentRemainingPassed,
        engineerResponding: currentEngineerResponding,
      };
    }
  }, [tests, requireTestingValue, remainingTestsPassedField?.value, engineerRespondingField?.value, handleFieldChange, getField]);

  const showProviderComment = !allTestsNeverReceived(tests);

  // Provider comment is independent of test state and carries forward month-to-month on the
  // backend, so it's only ever auto-populated once, the first time this field has no value at all.
  const hasInitializedProviderRef = useRef(false);
  useEffect(() => {
    if (!showProviderComment || hasInitializedProviderRef.current) return;
    hasInitializedProviderRef.current = true;
    if (!getField(PROVIDER_COMMENT_FIELD)?.value) {
      handleFieldChange(PROVIDER_COMMENT_FIELD, { value: DEFAULT_PROVIDER_COMMENT });
    }
  }, [showProviderComment, getField, handleFieldChange]);

  // Callback when tests change
  const handleTestsChange = (updatedTests: TestData[]) => {
    // This will trigger the useEffect above to regenerate comments
    // The tests state is derived from the field, so it will update automatically
  };

  const commentsField = getCommentsField(data, { isCommentField: (name, type) => type === FieldType.TextArray && name === COMMENTS_FIELD });
  const allEverAndCurrentNo = allTestsNeverReceivedAndNoneCurrent(tests);

  useEffect(() => {
    if (!allEverAndCurrentNo && requireTestingField?.value === 'Yes') {
      handleFieldChange(REQUIRE_TESTING_FIELD, { value: 'No' });
    }
  }, [allEverAndCurrentNo, requireTestingField?.value, handleFieldChange]);

  const autoCommentField = getField(AUTO_COMMENT_FIELD);
  const failedTestsCommentField = getField(FAILED_TESTS_COMMENT_FIELD);
  const allPassCommentField = getField(ALL_PASS_COMMENT_FIELD);
  const recommendationCommentField = getField(RECOMMENDATION_COMMENT_FIELD);

  // Whether each comment box is relevant is derived from the test data itself (the same
  // conditions the generator functions use), not from the field's current value - otherwise
  // clearing the text to retype it would make the box disappear with no way to bring it back.
  const hasFailedTests = tests.some(test => test.failedTests && test.failedTests.trim() !== '');
  const showAutoComment = tests.length > 0 || !!autoCommentField?.value;
  const showFailedTestsComment = hasFailedTests || !!failedTestsCommentField?.value;
  const showAllPassComment =
    (hasFailedTests ? remainingTestsPassedField?.value === 'Yes' : tests.some(test => test.anyCurrent === 'Yes')) ||
    !!allPassCommentField?.value;
  const showRecommendationComment = hasFailedTests || !!recommendationCommentField?.value;

  // Create layout dynamically with access to getField and handleFieldChange
  const layout = useMemo(() => {
    const fields: any[] = [
      {
        fieldName: TESTS_FIELD,
        type: 'custom-component' as const,
        label: '',
        renderComponent: () => (
          <MaterialTestingTable
            testsField={testsField}
            handleFieldChange={handleFieldChange}
            onTestsChange={handleTestsChange}
            requireTestingField={requireTestingField}
            isRequireTestingToggleEnabled={allEverAndCurrentNo}
            onRequireTestingChange={value => handleFieldChange(REQUIRE_TESTING_FIELD, { value })}
            remainingTestsPassedField={remainingTestsPassedField}
            onRemainingTestsPassedChange={value => handleFieldChange(REMAINING_TESTS_PASSED_FIELD, { value })}
            engineerRespondingField={engineerRespondingField}
            onEngineerRespondingChange={value => handleFieldChange(ENGINEER_RESPONDING_FIELD, { value })}
          />
        ),
      },
    ];

    // Visibility is derived from the test data (see showXComment above), not from the field's
    // current value - so clearing the text never removes the box.
    if (showAutoComment) {
      fields.push({
        fieldName: AUTO_COMMENT_FIELD,
        type: 'text-with-refresh' as const,
        label: '',
        onRefresh: handleRefreshAutoComment,
      });
    }
    if (showFailedTestsComment) {
      fields.push({
        fieldName: FAILED_TESTS_COMMENT_FIELD,
        type: 'text-with-refresh' as const,
        label: '',
        onRefresh: handleRefreshFailedTestsComment,
      });
      fields.push({
        fieldName: 'MaterialTestingFailedTestsTable',
        type: 'custom-component' as const,
        label: '',
        renderComponent: () => <MaterialTestingFailedTestsTable testsField={testsField} handleFieldChange={handleFieldChange} />,
      });
    }
    if (showAllPassComment) {
      fields.push({
        fieldName: ALL_PASS_COMMENT_FIELD,
        type: 'text-with-refresh' as const,
        label: '',
        onRefresh: handleRefreshAllPassComment,
      });
    }
    if (showRecommendationComment) {
      fields.push({
        fieldName: RECOMMENDATION_COMMENT_FIELD,
        type: 'text-with-refresh' as const,
        label: '',
        onRefresh: handleRefreshRecommendationComment,
      });
    }

    if (showProviderComment) {
      fields.push({
        fieldName: PROVIDER_COMMENT_FIELD,
        type: 'text-with-refresh' as const,
        label: '',
        defaultValue: DEFAULT_PROVIDER_COMMENT,
        errorIfContains: PROVIDER_PLACEHOLDER,
        onRefresh: handleRefreshProviderComment,
      });
    }

    return { singleColumn: fields };
  }, [
    testsField,
    handleFieldChange,
    handleRefreshAutoComment,
    handleRefreshFailedTestsComment,
    handleRefreshAllPassComment,
    handleRefreshRecommendationComment,
    handleRefreshProviderComment,
    showAutoComment,
    showFailedTestsComment,
    showAllPassComment,
    showRecommendationComment,
    showProviderComment,
    requireTestingField?.value,
    engineerRespondingField?.value,
    remainingTestsPassedField?.value,
    allEverAndCurrentNo,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <DeclarativeLayoutRenderer layout={layout} getField={getField} handleFieldChange={handleFieldChange} onBook={onBook} />

      {/* Comments Section */}
      {commentsField && (
        <CommentsSection commentsField={commentsField} onChange={updated => handleFieldChange(COMMENTS_FIELD, updated)} onBook={onBook} />
      )}
    </Box>
  );
};

export const MaterialTestingReportsSection: React.FC<MaterialTestingReportsSectionProps> = ({ magicReportId }) => {
  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={MATERIAL_TESTING_REPORTS_SECTION_CONFIG}
      customization={{
        onDataTransform: data => {
          const hasRequireTestingField = data.fields.some(field => field.fieldName === REQUIRE_TESTING_FIELD);
          const hasRemainingTestsPassedField = data.fields.some(field => field.fieldName === REMAINING_TESTS_PASSED_FIELD);
          const hasEngineerRespondingField = data.fields.some(field => field.fieldName === ENGINEER_RESPONDING_FIELD);

          if (hasRequireTestingField && hasRemainingTestsPassedField && hasEngineerRespondingField) {
            return data;
          }

          const fieldsToAdd: SectionFieldDTO[] = [];

          if (!hasRequireTestingField) {
            fieldsToAdd.push({
              fieldName: REQUIRE_TESTING_FIELD,
              fieldType: FieldType.Text,
              value: 'No',
              addToActionItems: false,
            });
          }

          if (!hasRemainingTestsPassedField) {
            fieldsToAdd.push({
              fieldName: REMAINING_TESTS_PASSED_FIELD,
              fieldType: FieldType.Text,
              value: 'Yes',
              addToActionItems: false,
            });
          }

          if (!hasEngineerRespondingField) {
            fieldsToAdd.push({
              fieldName: ENGINEER_RESPONDING_FIELD,
              fieldType: FieldType.Text,
              value: 'Yes',
              addToActionItems: false,
            });
          }

          return {
            ...data,
            fields: [...data.fields, ...fieldsToAdd],
          };
        },
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <MaterialTestingReportsSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
